<template>
  <Select
    v-if="!isMobile && !isDrawerOpen"
    id="customize"
    :selection="visibleColumns"
    :options="options"
    narrow
    @update:selection="onModelValueChange"
  >
    <template #trigger>
      <HeaderAction
        label="Customize"
        description="Customize table columns"
        icon="cog-6-tooth"
        hotkey="c"
      />
    </template>
  </Select>
</template>

<script setup lang="ts">
import { computed, isReadonly, toValue } from "vue";

import { useIsPortalOpen } from "@/design_system/Drawer/Portal";
import Select, { type SelectOption } from "@/design_system/Select";
import { mapDefined } from "@/lib/collections";
import { useMediaQuery } from "@/lib/composables/media-query";
import type { TableInstance } from "@/lib/tanstack-table";
import { isFixedColumn } from "@/lib/tanstack-table";

import HeaderAction from "./HeaderAction.vue";

const isMobile = useMediaQuery(`(width < 1024px)`);
const isDrawerOpen = useIsPortalOpen();

const props = defineProps<{
  table: TableInstance<any>;
}>();

const internal = props.table.internal;
const columnVisibilityState = internal.columnVisibility;

const visibleColumns = computed(() => {
  return columnVisibilityState?.value.columns ?? [];
});

const cols = computed(() => {
  const current = visibleColumns.value;

  return internal.instance.getAllColumns().filter((column) => {
    const id = column.id;
    const meta = column.columnDef.meta;

    if (isFixedColumn(column)) {
      return false;
    }

    // Respect enableHiding: false on column definitions
    if (column.columnDef.enableHiding === false) {
      return false;
    }

    const def = meta?.visibility;
    const canShow = toValue(def?.canShow) ?? true;

    return canShow || current.includes(id);
  });
});

const options = computed(() => {
  return cols.value.map((column): SelectOption => {
    const def = column.columnDef.meta?.visibility;

    return {
      id: column.id,
      label: def?.label ?? "" + column.columnDef.header,
      group: def?.group ?? "Info",
    };
  });
});

let first = true;
const onModelValueChange = (next: string[]) => {
  if (!columnVisibilityState || isReadonly(columnVisibilityState)) {
    return;
  }

  // One-time prune to the `next` array to only include columns that we know exists
  if (first) {
    const known = new Set(cols.value.map((col) => col.id));

    first = false;
    next = next.filter((id) => {
      // remove duplicate as well
      if (known.has(id)) {
        known.delete(id);
        return true;
      }

      return false;
    });
  }

  const current = columnVisibilityState.value;

  // If user hasn't reordered, maintain canonical column order
  if (!current.reordered) {
    next = mapDefined(cols.value, (col) => {
      if (!next.includes(col.id)) {
        return;
      }

      return col.id;
    });
  }

  columnVisibilityState.value = { ...current, columns: next };
};
</script>
